import { withPickedFileReadTurn } from "./pickedFileReadGate";

export interface CloudHostProbeTelemetry {
	onTimeout?(): void;
	onReadError?(error: unknown): void;
	onSettled?(observation: CloudHostProbeObservation): void;
}

export interface CloudHostProbeObservation {
	cloudFileClassification: "confirmed" | "inferred-timeout" | "local" | "unknown";
	classificationReason: "one-byte-read-timeout" | "one-byte-read-completed" | "one-byte-read-failed";
	probeDurationMs: number;
	probeTimeoutMs: number;
}

interface OneByteProbeSource {
	slice(
		start: number,
		end: number
	): {
		arrayBuffer(): Promise<ArrayBuffer>;
	};
}

export function probeCloudHostedFile(source: OneByteProbeSource, timeoutMs: number, telemetry?: CloudHostProbeTelemetry): Promise<boolean> {
	// LSP-22818: the timeout starts when this read's turn begins, so waiting in the Android read gate never reads as a cloud
	// file, and the gate stays held until the one-byte read itself settles, even after the probe has timed out.
	return withPickedFileReadTurn(() => probeOneByte(source, timeoutMs, telemetry));
}

function probeOneByte(
	source: OneByteProbeSource,
	timeoutMs: number,
	telemetry?: CloudHostProbeTelemetry
): { result: Promise<boolean>; settled: Promise<unknown> } {
	let settled: Promise<unknown> = Promise.resolve();
	const startedAt = Date.now();
	let observed = false;
	const observe = (classificationReason: CloudHostProbeObservation["classificationReason"]) => {
		if (observed) return;
		observed = true;
		try {
			telemetry?.onSettled?.({
				cloudFileClassification: classificationReason === "one-byte-read-timeout" ? "inferred-timeout" : "unknown",
				classificationReason,
				probeDurationMs: Math.max(0, Date.now() - startedAt),
				probeTimeoutMs: timeoutMs
			});
		} catch {
			/* Observation cannot change the strategy verdict. */
		}
	};
	const result = new Promise<boolean>((resolve, reject) => {
		const timer = setTimeout(() => {
			observe("one-byte-read-timeout");
			try {
				telemetry?.onTimeout?.();
			} catch {
				// Observability must not prevent the probe from settling.
			}
			resolve(true);
		}, timeoutMs);
		let read: Promise<ArrayBuffer>;
		try {
			read = source.slice(0, 1).arrayBuffer();
			settled = read;
		} catch (error) {
			clearTimeout(timer);
			observe("one-byte-read-failed");
			// Preserve the existing synchronous-source failure's rejected result.
			reject(error);
			return;
		}
		read.then(() => {
			clearTimeout(timer);
			observe("one-byte-read-completed");
			resolve(false);
		}).catch((error) => {
			clearTimeout(timer);
			observe("one-byte-read-failed");
			try {
				telemetry?.onReadError?.(error);
			} catch {
				// Observability must not prevent the probe from settling.
			}
			resolve(false);
		});
	});
	return { result, settled };
}
